import { Button } from '@kinetik/ui-kit';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import RefreshIcon from 'assets/RefreshIcon';
import Tooltip from 'components/v2/Tooltip';

/**
 * Icon-only refresh button with a "Refresh" tooltip on hover.
 *
 * Wraps the `@kinetik/ui-kit` `Button` (`tertiary` + `iconOnly`) so the
 * Provider Profile tabs have a single component to drop into their toolbars.
 * The tooltip is skipped when the button is disabled — MUI Tooltip can't fire
 * on disabled elements anyway, and wrapping in a `<span>` to make it work
 * adds an inline line-box that visibly offsets the icon.
 *
 * Uses the legacy `assets/RefreshIcon` because the ui-kit hasn't shipped a
 * refresh-style icon yet. Swap to the ui-kit equivalent when it lands.
 *
 * @param {Object} props
 * @param {() => void} props.onClick - Click handler.
 * @param {boolean} [props.isLoading=false] - Replaces the icon with a spinner
 *   and disables the button while truthy.
 * @param {boolean} [props.disabled=false] - Disables the button.
 */
const RefreshButton = ({ onClick, isLoading = false, disabled = false }) => {
  const button = (
    <Button
      variant="tertiary"
      size="medium"
      iconOnly
      onClick={onClick}
      isLoading={isLoading}
      disabled={disabled}
      prefixIcon={<RefreshIcon />}
    />
  );

  if (disabled || isLoading) return button;

  return (
    <Tooltip arrow placement="top" title="Refresh">
      {button}
    </Tooltip>
  );
};

RefreshButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default memo(RefreshButton);
